import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import DialogContent from '@mui/material/DialogContent'
import Typography from '@mui/material/Typography'
import { cvAPI } from 'api/CvAPI'
import { cvParserAPI } from 'api/CvParserAPI'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import newWithSimpleModal from 'Components/reusable/HOC/newWithSimpleModal'
import { useNotificationPopup } from 'Components/reusable/Notification'
import { useUser } from 'hooks'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ICv } from 'types/cvsInterfaces'
import { IError } from 'types/error'

/**
 * Modal for confirming translation copy.
 *
 * @returns Translation copy confirmation modal.
 * @notExported
 */
interface ICopyTranslationModalProps {
  /** CV id to update. */
  cvId: number
  /** Current language code. */
  currentLanguage: string
  /** Submit indicator from modal wrapper. */
  submitIndicator: boolean
  /** Reset submit indicator. */
  resetSubmit: () => void
  /** Set wrapper loading state. */
  setLoading: (loading: boolean) => void
  /** Current wrapper loading state. */
  loading: boolean
  /** Called with updated CV from backend. */
  onCopyComplete: (updatedCv: ICv) => void
  /** Close modal callback from wrapper. */
  onClose: (data?: unknown) => void
  /** Set extra action buttons in modal action area. */
  setExtraActions: (actionItem: React.ReactNode) => void
}

const CopyTranslationModal: React.FC<ICopyTranslationModalProps> = ({
  cvId,
  currentLanguage,
  submitIndicator,
  resetSubmit,
  setLoading,
  loading,
  onCopyComplete,
  onClose,
  setExtraActions,
}) => {
  const { t } = useTranslation()
  const { setSuccessNotificationPopup } = useNotificationPopup()
  const { features = [] } = useUser()
  const [sendTranslation, setSendTranslation] = useState<boolean>(false)
  const [result, setResult] = useState<ICv>()
  const [backendError, setBackendError] = useState<IError>()

  // Provide extra action button via effect to avoid updating parent state during render
  useEffect(() => {
    const action = (
      <Button
        onClick={() => setSendTranslation(true)}
        disabled={loading || !features.includes('parse')}
        variant="contained"
        color="success"
        aria-label={t('profile.copyTranslationModal.translationSubmit')}
      >
        {loading ? (
          <CircularProgress size={24} style={{ color: 'white' }} />
        ) : (
          t('profile.copyTranslationModal.translationSubmit')
        )}
      </Button>
    )
    setExtraActions(action)

    return () => setExtraActions(undefined)
  }, [loading, features, t, setExtraActions])

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (!submitIndicator) {
        return
      }

      try {
        setLoading(true)
        const updatedCv = await cvAPI.copyTranslation(cvId, currentLanguage, controller)
        onCopyComplete(updatedCv)
        setSuccessNotificationPopup()
        onClose({ updatedCv })
      } catch (error) {
        setBackendError(error as IError)
        resetSubmit()
      } finally {
        setLoading(false)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [
    currentLanguage,
    cvId,
    onClose,
    onCopyComplete,
    resetSubmit,
    setLoading,
    setSuccessNotificationPopup,
    submitIndicator,
  ])

  useEffect(() => {
    const controller = new AbortController()

    /**
     * Polls the job status endpoint until translation is complete
     *
     * @param jobId - Job ID from initial translate request
     * @notExported
     */
    const pollParseStatus = async (jobId: string) => {
      const pollInterval = setInterval(async () => {
        try {
          const status = await cvParserAPI.getParseStatus(jobId, controller)

          if (status.status === 'completed' && status.results) {
            clearInterval(pollInterval)
            setResult(status.results[0].createdCV)
            setLoading(false)
          } else if (status.status === 'error') {
            clearInterval(pollInterval)
            setBackendError({
              status: 500,
              error: new Error(status.error || 'Parsing failed'),
              key: 'error.translate',
              id: '',
            } as IError)
            setLoading(false)
          }
        } catch (error) {
          clearInterval(pollInterval)
          setBackendError(error as IError)
          setLoading(false)
        }
      }, 5000) // Poll every 5 seconds
    }

    ;(async () => {
      if (!sendTranslation) {
        return
      }

      try {
        setLoading(true)

        const { jobId } = await cvParserAPI.translateCV(cvId, currentLanguage, controller)

        // Start polling for job status
        await pollParseStatus(jobId)
      } catch (error) {
        setBackendError(error as IError)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [currentLanguage, cvId, sendTranslation, setLoading])

  useEffect(() => {
    if (result) {
      onCopyComplete(result)
      setSuccessNotificationPopup()
      onClose({ updatedCv: result })
    }
  }, [result, onClose, onCopyComplete, setSuccessNotificationPopup])

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <DialogContent>
      <Typography gutterBottom>{t('profile.copyTranslationModal.info')}</Typography>
      <Typography color="text.secondary">{t('profile.copyTranslationModal.info2')}</Typography>
    </DialogContent>
  )
}

export default newWithSimpleModal(CopyTranslationModal)
